/** Assessment Engine frontend types — aligned with backend API. */



export type AeTemplateStatus = 'draft' | 'published' | 'retired';

export type AeCampaignStatus = 'draft' | 'open' | 'closed' | 'archived';

/** How campaign admins are notified of respondent completions (campaign-wide). */
export type AeAdminCompletionDigestFrequency = 'every' | 'daily' | 'weekly' | 'monthly';


export type AeCampaignType = 'one_off' | 'recurring' | 'evergreen';

export type AePresentationStyle = 'question' | 'section' | 'subsection';

export interface AeFollowUpConfig {
  enabled: boolean;
  frequency_days: number;
  max_reminders: number;
}

export interface AeCompletionOptions {
  allow_invite_others: boolean;
  allow_cohort_updates: boolean;
  allow_share_link: boolean;
}

export interface AeRespondentConfig {
  follow_up?: AeFollowUpConfig;
  completion_options?: AeCompletionOptions;
  invite_target_campaign_id?: string | null;
}

export const AE_DEFAULT_RESPONDENT_CONFIG: AeRespondentConfig = {
  follow_up: { enabled: false, frequency_days: 7, max_reminders: 3 },
  completion_options: {
    allow_invite_others: true,
    allow_cohort_updates: true,
    allow_share_link: true,
  },
};

export type AeSeriesFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';

export type AeResponseStatus = 'in_progress' | 'complete' | 'incomplete';

export type AeQuestionType =
  | 'demographic'
  | 'short_text'
  | 'long_text'
  | 'open_text'
  | 'single_choice'
  | 'multi_choice'
  | 'multiple_choice'
  | 'radio'
  | 'rank'
  | 'scale'
  | 'nps'
  | 'matrix'
  | 'consent';

/** Builder-facing types (demographic/consent are not builder types). */
export type AeBuilderQuestionType =
  | 'short_text'
  | 'long_text'
  | 'single_choice'
  | 'multi_choice'
  | 'scale'
  | 'nps'
  | 'matrix';

export type AeChoiceDisplay = 'radio' | 'dropdown' | 'checkbox' | 'boxes';

export type AeScaleDisplayStyle = 'numbers' | 'radio' | 'slider' | 'emoji' | 'stars';

export interface AeQuestionDisplayConfig {
  choiceDisplay?: AeChoiceDisplay;
  scaleLength?: number;
  scaleDisplayStyle?: AeScaleDisplayStyle;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
  /** Optional label per scale point (index matches point order). */
  scaleValueLabels?: string[];
  reverseScored?: boolean;
  /**
   * Durable link to a Setup-tab shared scale preset (`default_config.scale_presets[].id`).
   * Keeps the named scale selected when preset labels/range change; Setup propagates fields.
   */
  scalePresetId?: string;
}



export interface AeBranding {

  organization_display_name?: string;

  logo_url?: string;

  intro_text?: string;

}



export interface AeTemplateSummary {

  id: string;

  code: string;

  name: string;

  status?: AeTemplateStatus | string;

  created_at?: string;

  updated_at?: string;

}



export interface AeQuestionOption {

  id: string;

  option_value: string;

  option_label: string;

  sort_order: number;

}



export interface AeTemplateQuestion {

  id: string;

  template_id?: string;

  section_id: string;

  question_key: string;

  question_text: string;

  question_type: AeQuestionType;

  sort_order: number;

  is_required: boolean;

  scale_min: number | null;

  scale_max: number | null;

  allow_na: boolean;

  validation_config?: Record<string, unknown> | null;

  display_config?: AeQuestionDisplayConfig | Record<string, unknown> | null;

  options: AeQuestionOption[];

}



export interface AeTemplateSection {

  id: string;

  template_id?: string;

  parent_section_id: string | null;

  code: string;

  name: string;

  overview?: string | null;

  sort_order: number;

  scoring_config?: Record<string, unknown> | null;

  subsections: AeTemplateSection[];

  questions: AeTemplateQuestion[];

}



export interface AeTemplateLogicRule {

  id: string;

  template_id: string;

  source_question_id: string;

  operator: string;

  condition_values: unknown[];

  target_question_id: string | null;

  target_section_id: string | null;

  action: 'show' | 'hide' | 'skip';

  priority: number;

  trigger_scope?: 'question' | 'subsection' | 'section' | null;

}



export interface AeTemplateDetail extends AeTemplateSummary {

  description?: string | null;

  status: AeTemplateStatus;

  published_at?: string | null;

  frozen_at?: string | null;

  parent_template_id?: string | null;

  default_config?: Record<string, unknown>;

  sections: AeTemplateSection[];

  logic_rules?: AeTemplateLogicRule[];

}



export interface AeEvaluatedQuestion {

  id: string;

  question_key: string;

  question_text: string;

  question_type: AeQuestionType;

  sort_order: number;

  is_required: boolean;

  scale_min: number | null;

  scale_max: number | null;

  allow_na: boolean;

  display_config?: AeQuestionDisplayConfig | Record<string, unknown> | null;

  options: AeQuestionOption[];

  visible: boolean;

}



export interface AeEvaluatedSection {

  id: string;

  code: string;

  name: string;

  overview?: string | null;

  sort_order: number;

  visible: boolean;

  questions: AeEvaluatedQuestion[];

  subsections: AeEvaluatedSection[];

}



export interface AeCampaign {

  id: string;

  tenant_id: string;

  template_id: string;

  series_id?: string | null;

  name: string;

  status: AeCampaignStatus;

  campaign_type: AeCampaignType;

  distribution_mode: 'invite_only' | 'public';

  is_anonymous: boolean;

  /** Always false — CAPTCHA permanently disabled. */
  captcha_required: boolean;

  /** When true (default), take flow requires email OTP before session start. */
  email_verification_required: boolean;

  public_link_token: string;

  branding?: AeBranding | Record<string, unknown> | null;

  time_period_config?: Record<string, unknown> | null;

  notification_config?: Record<string, unknown> | null;

  report_defaults?: Record<string, unknown> | null;

  presentation_style?: AePresentationStyle;

  respondent_config?: AeRespondentConfig | Record<string, unknown> | null;

  scheduled_open_at?: string | null;

  administered_by?: string | null;

  template?: AeTemplateSummary | null;

  opened_at?: string | null;

  closed_at?: string | null;

  created_at?: string;

  updated_at?: string;

}



export interface AeCampaignListItem extends AeCampaign {

  invite_count: number;

  response_count: number;

  complete_count: number;

  administrator_name?: string | null;

  administrator_email?: string | null;

  company_name?: string | null;

  template_name?: string | null;

  template_code?: string | null;

}



export interface AeCampaignListParams {

  status?: AeCampaignStatus;

  administered_by?: string;

  company?: string;

}

export interface AeCampaignAdmin {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_primary: boolean;
  created_at: string;
}

export interface AeCampaignAdminCandidate {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
}



/** Template-level copy shown before and after the respondent takes an assessment. */
export interface AeRespondentOpening {
  purpose?: string;
  instructions?: string;
}

export interface AeRespondentClosing {
  message?: string;
}

export interface AeRespondentFraming {
  opening?: AeRespondentOpening;
  closing?: AeRespondentClosing;
}

export interface AePublicCampaign {
  id: string;
  name: string;
  status: AeCampaignStatus;
  branding?: AeBranding | Record<string, unknown> | null;
  captcha_required: boolean;
  email_verification_required: boolean;
  distribution_mode: string;
  template: AeTemplateSummary | null;
  respondent_framing?: AeRespondentFraming | null;
}



export interface AeCampaignInvite {

  id: string;

  campaign_id: string;

  email: string;

  status: string;

  sent_at?: string | null;

  clicked_at?: string | null;

  created_at: string;

}



export interface AeCampaignFunnelSummary {
  invited: number;
  sent: number;
  clicked: number;
  started: number;
  complete: number;
}

export interface AeCampaignFunnelRow {
  invite_id: string;
  email: string;
  invite_status: string;
  sent_at?: string | null;
  clicked_at?: string | null;
  response_status?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  disabled_at?: string | null;
  opted_out_at?: string | null;
  opt_out_reason?: string | null;
}

export interface AeCampaignFunnel {
  summary: AeCampaignFunnelSummary;
  rows: AeCampaignFunnelRow[];
}



export interface AeCampaignLink {

  public_url: string;

  invite_count: number;

  response_count: number;

  complete_count: number;

}



export interface AeCohortSettings {

  b1MinResponses: number;

  b2MinCampaigns: number;

  b3MinOrganizations: number;

}



export interface AeCampaignSeries {

  id: string;

  tenant_id: string;

  template_id: string;

  name: string;

  frequency: AeSeriesFrequency;

  close_schedule: Record<string, unknown>;

  is_active: boolean;

  created_at: string;

  updated_at: string;

}



export interface AeReportConfig {

  id: string;

  campaign_id: string;

  benchmark_b1_enabled: boolean;

  benchmark_b2_enabled: boolean;

  benchmark_b3_enabled: boolean;

  benchmark_b1_min_responses: number;

  filter_defaults: Record<string, unknown> | null;

  show_instant_individual_results: boolean;

  include_admin_notes_in_share: boolean;

}



export interface AeResponseSummary {

  id: string;

  campaign_id: string;

  respondent_email?: string;

  status: AeResponseStatus;

  time_period_key?: string;

  started_at?: string;

  completed_at?: string | null;

  last_saved_at?: string;

  excluded_from_aggregate?: boolean;

  email_domain?: string | null;

}



export interface AeSessionStartResult {
  response_id: string;
  status: string;
  time_period_key?: string;
  resume_question_key: string | null;
  /** question_key → scalar for skip/logic (from saved answers). */
  answer_map?: Record<string, string | number | boolean | null>;
  sections: AeEvaluatedSection[];
  logic_rules?: AeTemplateLogicRule[];
  presentation_style?: AePresentationStyle;
  show_instant_results?: boolean;
  redirect_to_results?: boolean;
  completion_options?: Partial<AeCompletionOptions>;
  distribution_mode?: string;
}

/** Authenticated preview session — mirrors take flow without persisting responses. */
export interface AePreviewSessionResult {
  preview: true;
  name: string;
  sections: AeEvaluatedSection[];
  logic_rules?: AeTemplateLogicRule[];
  presentation_style?: AePresentationStyle;
  respondent_framing?: AeRespondentFraming | null;
  branding?: AeBranding | Record<string, unknown> | null;
}



export interface AeSectionScore {

  section_id: string;

  section_code: string;

  section_name: string;

  mean_score: number | null;

  response_count: number;

  gap?: number | null;

}



export interface AeBenchmarkOverlay {

  type: 'B1' | 'B2' | 'B3';

  cohort_met: boolean;

  cohort_count: number;

  threshold: number;

  sections: Array<{
    section_id: string;
    section_code: string;
    mean_score: number | null;
    std_dev?: number | null;
  }>;

}



export interface AeCampaignReport {

  campaign_id: string;

  as_of: string;

  response_count: number;

  complete_count: number;

  aggregate_eligible_count?: number;

  scale_max?: number;

  sections: Array<AeSectionScore & { gap?: number | null }>;

  questions?: AeQuestionScoreItem[];

  scoring_footnote: string;

  benchmark?: { b1: AeBenchmarkOverlay } | null;

  /** Instant-results-shaped B1 for shared AssessmentResultsView. */
  benchmark_b1?: {
    cohort_met: boolean;
    cohort_count: number;
    threshold: number;
    time_period_key: string;
    sections: Array<{
      section_id: string;
      section_code: string;
      mean_score: number | null;
      std_dev?: number | null;
    }>;
  } | null;

  cohort_status?: {

    b1: { met: boolean; count: number; threshold: number } | null;

  };

  narrative?: AeNarrativeState;

  individual?: { sections: AeSectionScore[] } | null;

  filters_applied?: Record<string, string> | null;

  /** Present when template maturity_map.enabled. */
  maturity?: import('@/components/assessment-engine/template-builder/maturityMapUtils').AeMaturityPayload | null;

}



export interface AeProgressionPoint {

  response_id: string;

  campaign_id: string;

  campaign_name: string;

  completed_at: string | null;

  time_period_key: string;

  sections: AeSectionScore[];

}



export interface AeQuestionScoreItem {
  display_number: string;
  section_id: string;
  section_name: string;
  question_id: string;
  question_key: string;
  question_type: string;
  question_text: string;
  statement_label: string | null;
  statement_key: string | null;
  score: number | null;
  is_na: boolean;
  gap: number | null;
  /** Item ceiling used for gap-to-max when known (NPS typically 10). */
  scale_max?: number | null;
  benchmark_score: number | null;
}

export interface AeInstantResults {
  response_id: string;
  campaign_id: string;
  scale_max: number;
  sections: AeSectionScore[];
  /** Numbered scored questions; each matrix statement is its own row. */
  questions?: AeQuestionScoreItem[];
  progression: AeProgressionPoint[];
  benchmark_b1: {
    cohort_met: boolean;
    cohort_count: number;
    threshold: number;
    time_period_key: string;
    sections: Array<{
      section_id: string;
      section_code: string;
      mean_score: number | null;
      std_dev?: number | null;
    }>;
  } | null;
  scoring_footnote: string;
  /** Present when template maturity_map.enabled. */
  maturity?: import('@/components/assessment-engine/template-builder/maturityMapUtils').AeMaturityPayload | null;
}



export interface AeResponseAnswerDetail {
  question_id: string;
  question_key: string;
  question_text: string;
  question_type: string;
  section_name: string;
  answer_text: string | null;
  answer_numeric: number | null;
  is_na: boolean;
  answered_at?: string | null;
}

export interface AeResponseDetail {
  response: AeResponseSummary & { respondent_email?: string };
  sections: AeSectionScore[];
  answers: AeResponseAnswerDetail[];
}



export interface AeCompleteResponseResult {

  id: string;

  status: string;

  show_instant_results?: boolean;

  instant_results?: AeInstantResults | null;

  completion_options?: Partial<AeCompletionOptions>;

  distribution_mode?: string;

}



export interface AeNarrativeState {

  ai_draft_text: string | null;

  published_text: string | null;

  published_at: string | null;

  narrative_stale: boolean;

  display_text: string | null;

  draft_source?: 'native' | 'ai' | null;

  assistive_label?: string;

}



export interface AeShareLink {

  id: string;

  share_token: string;

  recipient_email: string | null;

  viewer_scope: Record<string, unknown>;

  expires_at: string | null;

  live_url: string;

  created_at: string;

}



export interface AeLiveReport extends AeCampaignReport {}



export interface CreateCampaignInput {

  template_id: string;

  name: string;

  campaign_type?: AeCampaignType;

  distribution_mode?: 'invite_only' | 'public';

  is_anonymous?: boolean;

  /** Ignored — CAPTCHA permanently disabled. */
  captcha_required?: boolean;

  email_verification_required?: boolean;

  branding?: Record<string, unknown> | null;

  time_period_config?: Record<string, unknown> | null;

  notification_config?: Record<string, unknown> | null;

  report_defaults?: Record<string, unknown> | null;

  presentation_style?: AePresentationStyle;

  respondent_config?: AeRespondentConfig | Record<string, unknown> | null;

  series_id?: string | null;

}



export interface CreateTemplateInput {

  code: string;

  name: string;

  description?: string | null;

  default_config?: Record<string, unknown>;

}



export interface CreateSectionInput {

  code: string;

  name: string;

  overview?: string | null;

  sort_order?: number;

  parent_section_id?: string | null;

  scoring_config?: Record<string, unknown> | null;

}



export interface CreateQuestionInput {

  section_id: string;

  question_key: string;

  question_text: string;

  question_type: AeQuestionType;

  sort_order?: number;

  is_required?: boolean;

  scale_min?: number | null;

  scale_max?: number | null;

  allow_na?: boolean;

  display_config?: AeQuestionDisplayConfig | Record<string, unknown> | null;

  validation_config?: Record<string, unknown> | null;

  options?: CreateQuestionOptionInput[];

}



export interface CreateQuestionOptionInput {

  option_value: string;

  option_label: string;

  sort_order?: number;

}



export interface CreateSeriesInput {

  template_id: string;

  name: string;

  frequency: AeSeriesFrequency;

  close_schedule?: Record<string, unknown>;

  is_active?: boolean;

}


